// A custom hook for tracking changes to the job schedule
import { useCallback } from 'react';
import axiosInstance from '../axios/axiosConfig';
import { useSelector } from 'react-redux';

const normalizeScheduleLogValue = (value, field) => {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value).trim();

  if (field === 'DollarValue' && stringValue && !stringValue.startsWith('$')) {
    return `$${stringValue}`;
  }

  return stringValue;
};

/**
 * Hook for logging changes to the job schedule
 * @returns {Function} logScheduleChange - Function to log schedule changes
 */
const useScheduleChangeLog = () => {
  // Get API URLs from Redux store
  const apiUrls = useSelector((state) => state.apiUrls.urls);
  
  // Get user details from Redux store for audit logging
  const userDetails = useSelector((state) => state.user.userDetails);
  const userRoles = useSelector((state) => state.user.userRoles);
  const currentUser = userDetails?.name || "Unknown";
  const primaryRole = userRoles?.length > 0 ? userRoles[0] : "User";

  /**
   * Log a change to the job schedule
   * @param {number} projectId - The ID of the project that was changed
   * @param {string} jobNumber - The job number of the project
   * @param {string} jobName - The job name/project name
   * @param {string} field - The field that was changed
   * @param {string} prevValue - The previous value of the field
   * @param {string} newValue - The new value of the field
   * @returns {Promise} - The result of the API call
   */
  const logScheduleChange = useCallback(async (projectId, jobNumber, jobName, field, prevValue, newValue) => {
    try {
      if (!apiUrls.scheduleChangeLog) {
        console.error('scheduleChangeLog API URL is not defined');
        return;
      }

      const changeData = {
        ProjectID: projectId,
        JobNumber: jobNumber || '',
        JobName: jobName || '',
        CurrentUser: currentUser,
        UserRole: primaryRole,
        Field: field,
        PrevValue: normalizeScheduleLogValue(prevValue, field),
        NewValue: normalizeScheduleLogValue(newValue, field),
        DateTime: new Date().toISOString()
      };      // Make the API call to log the change
      const response = await axiosInstance.post(apiUrls.scheduleChangeLog, changeData);
      return response.data;
    } catch (error) {
      console.error('Error logging schedule change:', error);
      // We don't want to block the UI if logging fails, so we just log the error
    }
  }, [apiUrls.scheduleChangeLog, currentUser, primaryRole]);

  return { logScheduleChange };
};

export default useScheduleChangeLog;
